import React from "react";
// import { DEFAULT_UNWEIGHTED_NOTES } from "../../functions/backup_jsaudioapi/convertHistogramToNotes";
import { AudioProcessOutput, MusicScale, UserSettings } from "../../../wasm/pkg/wasm";
import { AppContextType, AppLoadingState } from "../../AppContext";
import chooseAutomaticNoteOverrides from "../../functions/chooseAutomaticNoteOverrides";
import { ChordEditBlastRadius } from "../../models/ChordHistoryOptions.model";
import { AudioWebWorkerInputControls } from "../../models/PostMessages.interface";
import { ChordInScaleEdit, NoteSettingsDecision } from "../../models/wasm.model";
import {
  AudioHistoryState,
  buildBlankAudioProcessingState,
  buildNextAudioProcessingInfo,
  buildPlaceholderAudioProcessingState,
  mutateAudioHistoryDeleteChord,
  mutateAudioHistoryDeleteScale,
} from "../../statemachines/AudioHistoryState";
import { connectToAudioWebWorker } from "../../worker/connectToAudioWebWorker";
import InfoCard from "../layout_reuse/InfoCard";
import AudioControlBar, { AudioFeedStatus, LatestAudioInfo } from "../music_controls/AudioControlBar";
import NoteValidityControls from "../music_controls/NoteValidityControls";
import HistoryMonitor from "../music_visuals/history_monitor/HistoryMonitor";
import { ChordPopupArgs } from "../music_visuals/history_monitor/HistoryMonitorColumn";
import MusicKeyGiantLetter from "../music_visuals/MusicKeyGiantLetter";
import TinyHistogram from "../music_visuals/TinyHistogram";
import ChordEditPopup from "../popups/ChordEditPopup";
import WasmLoadingStatus from "../popups/WasmLoadingStatus";
import "./AnalysisPage.css";
import TranscriptionPopup from "../popups/TranscriptionPopup";
import InstrumentationWorkspace, { ChordToBeInstrumented } from "../instruments/InstrumentationWorkspace";
interface AnalysisPageProps {}
interface AnalysisPageState {
  webHistogramData: Uint8Array;
  rustHistogramData: Array<number>;
  audioHistory: AudioHistoryState;
  sampleRate: number;
  workerControls?: AudioWebWorkerInputControls;
  isStreamConnectedToWorker: boolean;
  /** whether the recording is actively being processed */
  audioFeedStatus: AudioFeedStatus;
  autoPauseResumeIncrement: number;
  noteSettings: Array<NoteSettingsDecision>;
  chordEditPopup?: ChordPopupArgs;
  chordsToBeInstrumented: Array<ChordToBeInstrumented>;
  instrumentationCounter: number;
}

export default class AnalysisPage extends React.Component<AnalysisPageProps, AnalysisPageState> {
  declare context: React.ContextType<typeof AppContextType>;
  constructor(props: AnalysisPageProps) {
    super(props);
    this.state = {
      webHistogramData: new Uint8Array(),
      rustHistogramData: [],
      audioHistory: buildPlaceholderAudioProcessingState(),
      sampleRate: 0,
      workerControls: undefined,
      isStreamConnectedToWorker: false,
      audioFeedStatus: AudioFeedStatus.INACTIVE,
      autoPauseResumeIncrement: 0,
      noteSettings: [],
      chordEditPopup: undefined,
      chordsToBeInstrumented: [],
      instrumentationCounter: 0,
    };
  }

  onAudioWorkerInit = () => {
    this.context.dispatch({ type: "UpdateLoadingState", payload: AppLoadingState.READY });
  };

  onAudioWorkerError = (err: string) => {
    const { autoPauseResumeIncrement } = this.state;
    if (err === "FatalAudioProcessError") {
      if (autoPauseResumeIncrement < 10) {
        if (autoPauseResumeIncrement === 6 && this.context.browser.streamFormat !== "RAW") {
          console.warn("Repeated stream errors: failing over from WebM encoding to raw PCM (slightly more stable, with worse continuity).");
          this.context.dispatch({ type: "FailoverToRawStream" }); // Assume webm is stuck on some tag it can't parse
        }
        this.setState({
          autoPauseResumeIncrement: 1 + autoPauseResumeIncrement,
          isStreamConnectedToWorker: false,
        });
      } else {
        this.context.dispatch({
          type: "AddToErrorList",
          payload: ["Fatal error: giving up after 10 autorecovery attempts."],
        });
      }
    } else if (err === "WASM_INIT_ERROR") {
      this.context.dispatch({ type: "UpdateLoadingState", payload: AppLoadingState.FAILED });
      this.context.dispatch({
        type: "AddToErrorList",
        payload: ["Fatal error: failed to launch WebAssembly worker."],
      });
    }
  };

  onAudioWorkerData = (output: AudioProcessOutput) => {
    // console.log("analysispage sees onAudioWorkerData");
    const audioHistory = this.state.audioHistory.isPlaceholder ? buildBlankAudioProcessingState() : this.state.audioHistory;
    this.setState({ rustHistogramData: output.histogram, audioHistory: buildNextAudioProcessingInfo(audioHistory, output) });

    const noteSettingsAuto = chooseAutomaticNoteOverrides(
      this.state.audioHistory,
      this.state.noteSettings,
      this.context.keySelectionOptions,
    );
    if (noteSettingsAuto?.length > 0) {
      console.log("applying auto pin decisions:", noteSettingsAuto);
      this.handleChangeNoteSettings(noteSettingsAuto);
    }
  };

  componentDidMount(): void {
    const workerControls = connectToAudioWebWorker({
      onInit: this.onAudioWorkerInit,
      onError: this.onAudioWorkerError,
      onLatestMusicalData: this.onAudioWorkerData,
    });
    this.setState({ workerControls });
  }
  componentWillUnmount(): void {
    window.onbeforeunload = null;
  }

  handleLatestAudioData = ({ sampleRate: _, partialRecordingBlob }: LatestAudioInfo) => {
    const isWorkerReady = this.context.loading === AppLoadingState.READY;
    const { streamFormat } = this.context.browser;
    const { workerControls, isStreamConnectedToWorker } = this.state;
    if (isWorkerReady && workerControls) {
      // console.log("UI: sending a blob with size=", partialRecordingBlob.size);
      workerControls.sendNextPartialAudioBlob({ isFirstBlob: !isStreamConnectedToWorker, streamFormat, blob: partialRecordingBlob });
      if (!isStreamConnectedToWorker) {
        this.setState({ isStreamConnectedToWorker: true });
      }
    }
    if (!window.onbeforeunload) {
      // Avoid accidentally swiping left and deleting all work
      window.onbeforeunload = function () {
        return true; // can't control this with logic.  If func is defined -> nav prevention
      };
    }
  };
  handleAudioStreamComplete = () => {
    this.setState({ isStreamConnectedToWorker: false, rustHistogramData: [] });
    this.state.workerControls?.resetAudioState();
  };
  handleAudioFeedStateChange = (audioFeedStatus: AudioFeedStatus) => {
    this.setState({ audioFeedStatus });
  };

  handleHistoryReset = () => {
    window.onbeforeunload = null;
    this.setState({
      audioHistory: buildPlaceholderAudioProcessingState(),
    });
  };

  handleAudioSeek = (integerTime: number) => {
    if (this.context.browser.streamFormat === "RAW") {
      // WEBM PCM mode has logic to clear the stream, so its re-record is predictable
      // But a clean flush is kinda impossible with RAW.
      // RAW mode output is non-deterministic anyway (timers + lookback), so fuck it.
      this.state.workerControls!.resetAudioState();
      this.setState({ isStreamConnectedToWorker: false });
    }
    this.state.workerControls!.audioTimeSeekJump(integerTime);
  };

  handleChangeNoteSettings = (noteSettings: Array<NoteSettingsDecision>) => {
    // TODO: retroactive?  Harder, requires more historical info/infra.  Currently use can seek to re-do.
    const userSettings: UserSettings = {
      notes: noteSettings.map((ns) => ns.settings),
    };
    this.state.workerControls?.updateSettings(userSettings);
    this.setState({ noteSettings });
  };

  handleDeleteChord = (scaleId: string, tickIndex: number, chordIndex: number) => {
    const { audioHistory } = this.state;
    mutateAudioHistoryDeleteChord(audioHistory, { scaleId, tickIndex, chordIndex });
    this.setState({ audioHistory: { ...audioHistory } });
  };

  handleDeleteScale = (scaleId: string) => {
    const { audioHistory } = this.state;
    mutateAudioHistoryDeleteScale(audioHistory, scaleId);
    this.setState({ audioHistory: { ...audioHistory } });
  };

  handleOpenChordPopup = (chordEditPopup: ChordPopupArgs) => {
    this.setState({
      chordEditPopup,
    });
  };
  handleCloseChordPopup = () => {
    this.setState({ chordEditPopup: undefined });
  };
  handleSaveChordPopup = (chord: ChordInScaleEdit, opts: { blastRadius: ChordEditBlastRadius }) => {
    const { scaleId, timeIndex, chordIndex } = this.state.chordEditPopup!;
    const audioHistory = { ...this.state.audioHistory };
    if (opts.blastRadius === ChordEditBlastRadius.AllInstances) {
      const oldChord = audioHistory.chordsInScales[scaleId].chordHistory[timeIndex][chordIndex];
      const {
        chord_type,
        root_note: { note_id },
      } = oldChord;
      for (let i = 0; i < audioHistory.entryCounter; i++) {
        const matchI = audioHistory.chordsInScales[scaleId].chordHistory[i].findIndex(
          (c) => c.chord_type === chord_type && c.root_note.note_id === note_id,
        );
        if (matchI === 0 || matchI) {
          audioHistory.chordsInScales[scaleId].chordHistory[i][matchI] = chord;
        }
      }
    } else {
      // console.log("chord before", audioHistory.chordsInScales[scaleId].chordHistory[timeIndex][chordIndex]);
      // console.log("chord coming in", chord);
      audioHistory.chordsInScales[scaleId].chordHistory[timeIndex][chordIndex] = chord;
    }
    this.setState({
      audioHistory,
    });
  };
  handleSaveTranscription = (audioHistory: AudioHistoryState) => {
    this.handleChangeNoteSettings([]); // remove pins for visual clarity - recording will be fubar regardless
    this.setState({ audioHistory });
  };

  handleEnqueueInstrumentationChord = (chord: ChordInScaleEdit, scale: MusicScale) => {
    const chordsToBeInstrumented = [...this.state.chordsToBeInstrumented];
    const instrumentationCounter = this.state.instrumentationCounter + 1;
    chordsToBeInstrumented.push({ chord, scale, uniqueId: instrumentationCounter });
    this.setState({ chordsToBeInstrumented, instrumentationCounter });
  };
  handleDequeueInstrumentationChord = (uniqueId: number) => {
    const index = this.state.chordsToBeInstrumented.findIndex((c) => c.uniqueId === uniqueId);
    if (index > -1) {
      const chordsToBeInstrumented = this.state.chordsToBeInstrumented.toSpliced(index, 1);
      this.setState({ chordsToBeInstrumented });
    }
  };

  render() {
    // const { musicNoteOccurrences, sampleRate } = this.state;
    const {
      rustHistogramData,
      audioHistory,
      noteSettings,
      isStreamConnectedToWorker,
      audioFeedStatus,
      autoPauseResumeIncrement,
      chordEditPopup,
      chordsToBeInstrumented,
    } = this.state;
    const scale = audioHistory.primaryScale;
    return (
      <>
        <WasmLoadingStatus />

        <div className="analysis-page-grid">
          <InfoCard tightWrap className="area-control-bar">
            <AudioControlBar
              onLatestAudioInfoChange={this.handleLatestAudioData}
              onStreamComplete={this.handleAudioStreamComplete}
              onAudioFeedStateChange={this.handleAudioFeedStateChange}
              onHistoryReset={this.handleHistoryReset}
              onAudioSeek={this.handleAudioSeek}
              autoPauseResumeIncrement={autoPauseResumeIncrement}
            />
          </InfoCard>

          <InfoCard className="area-histogram">
            <TinyHistogram histogramData={rustHistogramData} />
          </InfoCard>

          <MusicKeyGiantLetter scale={scale} />

          {/*TODO: rename to KeyIdentificationControls - I can never find it - perhaps top level components need standard name */}
          <NoteValidityControls
            scale={scale}
            noteSettings={noteSettings}
            totalNoteWeights={audioHistory.weightHistory.totalNoteWeights}
            onChangeNoteSettings={this.handleChangeNoteSettings}
          />

          <HistoryMonitor
            audioHistory={audioHistory}
            isCaptureActive={isStreamConnectedToWorker && audioFeedStatus === AudioFeedStatus.ACTIVE}
            onDeleteChord={this.handleDeleteChord}
            onDeleteScale={this.handleDeleteScale}
            openChordPopup={this.handleOpenChordPopup}
            onChooseChordForInstrumentation={this.handleEnqueueInstrumentationChord}
          />
          <TranscriptionPopup audioHistory={audioHistory} onTranscribe={this.handleSaveTranscription} />
          {chordEditPopup ? (
            <ChordEditPopup
              centerX={chordEditPopup.clickX}
              centerY={chordEditPopup.clickY}
              chord={audioHistory.chordsInScales[chordEditPopup.scaleId].chordHistory[chordEditPopup.timeIndex][chordEditPopup.chordIndex]}
              onSave={this.handleSaveChordPopup}
              onClose={this.handleCloseChordPopup}
            />
          ) : undefined}

          <InstrumentationWorkspace
            chordsToAdd={chordsToBeInstrumented}
            onChordAddSuccess={this.handleDequeueInstrumentationChord}
            // selectedChordIndex={selectedInstrChordIndex}
            // lastDeletedIndex={deletedInstrChordIndex}
            // onDeleteChord={this.handleDeleteInstrumentationChord}
            // onEditChord={this.handleEditInstrumentationChord}
            // onReset={this.handleResetInstrumentationChords}
            // onSelect={this.handleSelectInstrumentationChord}
          />
        </div>
      </>
    );
  }
}

AnalysisPage.contextType = AppContextType;
